import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    LinearProgress,
    Avatar,
} from "@mui/material";
import api from "../../api";
import { getPredefinedAvatarImageUrl } from "../../utils/studentAvatarOptions";

const RankMedal = ({ rank, size = 26 }) => {
    let medalColor = "#facc15"; // gold-ish base
    if (rank === 2) medalColor = "#e5e7eb"; // silver
    if (rank === 3) medalColor = "#f97316"; // bronze

    const circleSize = size;
    const ribbonHeight = size * 0.45;
    const ribbonWidth = size * 0.45;

    return (
        <Box
            sx={{
                position: "relative",
                width: circleSize,
                height: circleSize + ribbonHeight * 0.4,
                display: "inline-flex",
                alignItems: "flex-start",
                justifyContent: "center",
            }}
        >
            {/* ribbon */}
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    width: ribbonWidth,
                    height: ribbonHeight,
                    borderBottomLeftRadius: ribbonWidth * 0.2,
                    borderBottomRightRadius: ribbonWidth * 0.2,
                    background:
                        "linear-gradient(180deg, #6366f1 0%, #ec4899 50%, #ef4444 100%)",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
                }}
            />
            {/* medal circle */}
            <Box
                sx={{
                    mt: ribbonHeight * 0.4,
                    width: circleSize,
                    height: circleSize,
                    borderRadius: "50%",
                    background: `radial-gradient(circle at 30% 25%, #fff7d6, ${medalColor})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow:
                        "0 2px 4px rgba(0,0,0,0.22), 0 0 0 1px rgba(255,255,255,0.8) inset",
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        fontSize: circleSize * 0.5,
                        fontWeight: 800,
                        color: "#7c2d12",
                        textShadow: "0 1px 2px rgba(255,255,255,0.8)",
                    }}
                >
                    {rank}
                </Typography>
            </Box>
        </Box>
    );
};

const GameLeaderboard = () => {
    const [leaderboard, setLeaderboard] = useState([]);
    const [currentUserRow, setCurrentUserRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await api.get("student/games/leaderboard");

                const raw =
                    res.data?.data?.leaderboard ||
                    res.data?.data ||
                    res.data?.leaderboard ||
                    [];

                const list = Array.isArray(raw) ? raw : [];

                // Normalise fields: id, name (pet name or full name), profile_image_url, total_points
                const normalised = list
                    .map((item) => ({
                        id: item.id || item.student_id || item.user_id,
                        name:
                            item.full_name ||
                            item.name ||
                            item.student_name ||
                            "Unknown",
                        profileImageUrl: item.profile_image_url || getPredefinedAvatarImageUrl(item.profile_image) || null,
                        profileImage: item.profile_image || null,
                        totalPoints: Number(
                            typeof item.total_points === "number"
                                ? item.total_points
                                : typeof item.score === "number"
                                ? item.score
                                : item.total_points || item.score || 0
                        ),
                    }))
                    .sort((a, b) => {
                        if (b.totalPoints !== a.totalPoints) {
                            return b.totalPoints - a.totalPoints;
                        }
                        return a.name.localeCompare(b.name);
                    });

                // Assign ranks with ties (same totalPoints => same rank)
                let lastPoints = null;
                let lastRank = 0;
                const withRank = normalised.map((row, index) => {
                    if (lastPoints === null || row.totalPoints < lastPoints) {
                        lastRank = index + 1;
                        lastPoints = row.totalPoints;
                    }
                    return {
                        ...row,
                        rank: lastRank,
                    };
                });

                setLeaderboard(withRank);

                // Find current user
                let userData = {};
                try {
                    userData = JSON.parse(localStorage.getItem("userData") || "{}");
                } catch {
                    userData = {};
                }
                const currentId =
                    userData.id || userData.student_id || userData.user_id || null;

                let me =
                    (currentId &&
                        withRank.find(
                            (row) => String(row.id) === String(currentId)
                        )) ||
                    (userData.full_name &&
                        withRank.find(
                            (row) => row.name.toLowerCase() === userData.full_name.toLowerCase()
                        )) ||
                    null;

                if (!me && withRank.length) {
                    // Not in list (maybe no games yet) – show placeholder with 0 points
                    me = {
                        id: currentId || "me",
                        name: userData.full_name || "You",
                        totalPoints: 0,
                        rank: "-",
                    };
                }

                setCurrentUserRow(me);
            } catch (err) {
                console.error("Failed to load game leaderboard", err);
                setError("Unable to load leaderboard right now.");
            } finally {
                setLoading(false);
            }
        };

        fetchLeaderboard();
    }, []);

    return (
        <Box sx={{ maxWidth: 900, mx: "auto", mt: 2 }}>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 2, color: "#1e293b" }}>
                🏅 Game Leaderboard
            </Typography>
            {currentUserRow && (
                <Paper
                    sx={{
                        mb: 3,
                        p: 2.5,
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background:
                            "linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)",
                        border: "1px solid #fbbf24",
                    }}
                >
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: "#92400e", mb: 0.5 }}
                        >
                            Your Rank
                        </Typography>
                        {currentUserRow.rank === "-" ? (
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 800, color: "#b45309" }}
                            >
                                Not ranked yet · {currentUserRow.name || "You"}
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {(() => {
                                    const r = currentUserRow.rank;
                                    const isTopThree = r === 1 || r === 2 || r === 3;
                                    if (!isTopThree) {
                                        return (
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 800, color: "#b45309" }}
                                            >
                                                #{r}
                                            </Typography>
                                        );
                                    }
                                    return <RankMedal rank={r} size={26} />;
                                })()}
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 800, color: "#b45309" }}
                                >
                                    {currentUserRow.name || "You"}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, color: "#92400e" }}
                        >
                            Total Points
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 800, color: "#b45309" }}
                        >
                            {currentUserRow.totalPoints}
                        </Typography>
                    </Box>
                </Paper>
            )}

            {loading ? (
                <Box sx={{ mt: 4 }}>
                    <LinearProgress />
                    <Typography sx={{ mt: 2, color: "#374151" }}>
                        Loading leaderboard...
                    </Typography>
                </Box>
            ) : error ? (
                <Typography sx={{ mt: 3, color: "#b91c1c" }}>{error}</Typography>
            ) : (
                <Paper sx={{ borderRadius: 4, overflow: "hidden" }}>
                    <Table
                        sx={{
                            "& .MuiTableCell-root": {
                                py: 0.75, // smaller row height
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: 90 }}>
                                    Rank
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    Total Points
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leaderboard.map((row) => {
                                const isTopThree =
                                    row.rank === 1 || row.rank === 2 || row.rank === 3;
                                let medalColor = "#d4af37"; // gold
                                if (row.rank === 2) medalColor = "#c0c0c0"; // silver
                                if (row.rank === 3) medalColor = "#cd7f32"; // bronze

                                const isCurrent =
                                    currentUserRow &&
                                    currentUserRow.rank !== "-" &&
                                    row.id &&
                                    String(row.id) === String(currentUserRow.id);

                                return (
                                    <TableRow key={`${row.id}-${row.rank}`} selected={!!isCurrent}>
                                        <TableCell>
                                            {isTopThree ? (
                                                <RankMedal rank={row.rank} size={24} />
                                            ) : (
                                                <Typography sx={{ fontWeight: 600 }}>
                                                    {row.rank}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Avatar
                                                    src={row.profileImageUrl}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        bgcolor: row.profileImageUrl ? "transparent" : "primary.main",
                                                        fontSize: "0.875rem",
                                                    }}
                                                >
                                                    {!row.profileImageUrl && (row.name || "?").charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {row.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.totalPoints}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {!leaderboard.length && (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Typography
                                            align="center"
                                            sx={{ py: 3, color: "#374151" }}
                                        >
                                            No game scores available yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
};

export default GameLeaderboard;

